import { AppPresence, GuestProfile, LimitedAgentProfile } from "@closerplatform/spinner-openapi";
import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useEffect, useState, lazy, Suspense } from "react";
import { useStore } from "../../models";
import { ImageComponent } from "@fidget/common/components/image/image";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { IBreakingPointsInfo } from "@fidget/common/models/breakingPoints";
import { logger } from "@fidget/common/utils";
import { isAudioCallSupported } from "@fidget/common/utils/browserSupport";
import { WidgetMenu } from "../menu/widgetMenu";
import { WidgetButton } from "@fidget/common/components/widgetButton/widgetButton";
import { AgentsHeader } from "./agentsHeaderSection";
import { CallingHeader } from "./callingHeader";
import styles from "./controlsHeader.css";
import { MessageTag } from "@fidget/common/models/messageTag";
import { SafeSessionStorageService } from "@fidget/common/models/safeSessionStorageService";
import { PhoneIcon } from "./phoneIcon/phoneIcon";

import { staticConfig } from "@fidget/common/config/static-config";
import { CenterPreloader } from "../centerPreloader/centerPreloader";
import { hasProfileMailOrPhone } from "@fidget/common/models/userProfile";
import { fileService } from "@fidget/common/services/files/fileService";
import { getWheelhouseDownloadUrl } from "@fidget/common/utils/urlMappingHelpers";
interface Props {
  agents: Array<LimitedAgentProfile>;
  showCloseButton?: boolean;
  breakingPoints: IBreakingPointsInfo;
  onResize?: () => void;
  forgetMeAction: () => void;
}

const AsyncContinueConversationHeader = lazy(
  () => import("../continue/continueConversationHeader")
);

export const ControlsHeader = observer((props: Props) => {
  const safeSessionStorage = SafeSessionStorageService.getInstance();
  const { foreCallStore, uiStore, contextStore } = useStore();
  const { foreContext: context } = contextStore;
  const {
    state: {
      shared: { orgConfig: shortOrgConfig }
    }
  } = context;
  const localStorageKeyTriedToAutoCall =
    staticConfig.localStorageKeyPrefix + ":" + context.orgId + ":triedToCallByAutoCalling";

  const [askForMicrophone, setAskForMicrophone] = useState<boolean>(false);
  const [isContinueConversationHeaderOpen, setIsContinueConversationHeaderOpen] =
    useState<boolean>(false);
  const [shouldAutoCall, setShouldAutoCall] = useState<boolean>(false);
  const [backupProfile, setBackupProfile] = useState<GuestProfile>();
  const [coverUrl, setCoverUrl] = useState<string>();

  if (!shortOrgConfig) {
    return <></>;
  }

  useEffect(() => {
    if (shortOrgConfig.coverToken) {
      fileService.getDataUrl(getWheelhouseDownloadUrl(shortOrgConfig.coverToken)).then((url) => setCoverUrl(url));
    }
  }, []);

  useEffect(() => {
    context.hostWindowRequests.onOpenWidgetWithAutoCalling(() => {
      setShouldAutoCall(true);
      return Promise.resolve();
    });
    handleNewAssignee();
  }, []);

  const handleCallClicked = () => {
    foreCallStore.reloadGrantedPermissions().then(() => {
      if (foreCallStore.localMicrophone.isPermissionGranted || askForMicrophone) {
        foreCallStore.connect(context.state.shared.assignedAgent!.id).then(() => {
          safeSessionStorage.setItem(localStorageKeyTriedToAutoCall, "true");
          setAskForMicrophone(false);
          setShouldAutoCall(false);
        });
      } else {
        setAskForMicrophone(true);
      }
    });
  };

  const checkShouldAutoCall = () => {
    const triedToCall = safeSessionStorage.getItem(localStorageKeyTriedToAutoCall);
    const didNotCalled = triedToCall !== "true";
    if (context.alwaysAutoCall) {
      return shouldAutoCall;
    }
    return didNotCalled && context.autoClientCalling && shouldAutoCall;
  };

  const handleNewAssignee = () => {
    if (context.callingOnAssigneeChanged) {
      const { artichokeService } = context;
      artichokeService.onConnected(() =>
        artichokeService.onCustom(context.state.shared.roomId!, MessageTag.ASSIGNEE_CHANGED, () =>
          setTimeout(() => {
            callOnNewAssignee(context.state.shared.assignedAgent);
          }, 2000)
        )
      );
    }
  };

  const callOnNewAssignee = (assignedAgent?: LimitedAgentProfile) => {
    if (
      assignedAgent !== undefined &&
      !assignedAgent.isBot &&
      assignedAgent.firstName !== context.botAgentName
    ) {
      handleCallClicked();
    }
  };

  const render = () => {
    const assignedAgent = context.state.shared.assignedAgent;

    if (
      assignedAgent &&
      (foreCallStore.isNoCall ||
        (!foreCallStore.isNoCall &&
          foreCallStore.videoExpanded &&
          !props.breakingPoints.shouldCallDrawerBeHidden))
    ) {
      return renderWithAssignee(assignedAgent);
    } else if (!foreCallStore.isNoCall) {
      return renderCallInProgress();
    }

    return renderNoAssignee(coverUrl);
  };

  const renderCallInProgress = () => {
    return (
      <div
        className={classNames(styles["controls-header"], styles["call-header"], {
          [styles["mobile-header"]]: props.breakingPoints.isMobile
        })}
      >
        <CallingHeader context={context} breakingPoints={props.breakingPoints} />
      </div>
    );
  };

  const continueConversationOnOtherDevice = async () => {
    let {
      state: { profile },
      spinnerService
    } = context;
    if (profile) {
      if (!hasProfileMailOrPhone(profile)) {
        // fetch the profile again to make sure that mail and profile weren't updated in the meantime.
        profile = await spinnerService.getGuestProfile(profile.id);
        if (hasProfileMailOrPhone(profile)) {
          setBackupProfile(profile);
        }
      }

      if (hasProfileMailOrPhone(profile)) {
        sendConversationLink();
      }
    }

    setIsContinueConversationHeaderOpen(true);
  };

  const sendConversationLink = (contact?: string) => {
    const { spinnerService } = context;
    try {
      spinnerService.sendContinueConversationLink(contact ? contact : undefined);
    } catch (error) {
      logger.error("Could not send continue conversation link");
      throw new Error(error as string);
    }
  };

  const getCallButtonTooltipText = (assignedAgent: LimitedAgentProfile) => {
    return !isAudioCallSupported ? (
      <L10n id={"widget.call.WebRTCNotSupported"} />
    ) : (
      <L10n id={"widget.call.isCurrentlyAway"} values={{ name: assignedAgent.firstName }} />
    );
  };

  const renderCallButton = (assignedAgent: LimitedAgentProfile, disabled: boolean) => (
    <TooltipComponent
      text={getCallButtonTooltipText(assignedAgent)}
      positionPreset="bottom"
      maxWidth="unset"
      width="120px"
      style="widget"
      tooltipShow={disabled}
    >
      {renderCallButtonIcon(disabled)}
    </TooltipComponent>
  );

  const renderCallButtonIcon = (disabled: boolean) => (
    <div onClick={disabled ? undefined : handleCallClicked}>
      <PhoneIcon disabled={disabled} />
    </div>
  );

  const renderContinueConversationHeader = (profile?: GuestProfile) => (
    <Suspense fallback={<CenterPreloader />}>
      <AsyncContinueConversationHeader
        profile={profile}
        agreementText={shortOrgConfig.agreement}
        onClose={() => {
          setIsContinueConversationHeaderOpen(false);
          setBackupProfile(undefined);
        }}
        sendConversationLink={sendConversationLink}
      />
    </Suspense>
  );

  const renderWithAssignee = (assignedAgent: LimitedAgentProfile) => {
    const assignedAgentAvailable = assignedAgent.presence === AppPresence.Available;
    const profile = backupProfile || context.state.profile;
    const autoCall =
      isAudioCallSupported && assignedAgent && assignedAgentAvailable && checkShouldAutoCall();

    if (autoCall) {
      setTimeout(handleCallClicked, 1500);
    }

    return (
      <>
        {isContinueConversationHeaderOpen ? (
          renderContinueConversationHeader(profile)
        ) : (
          <>
            <div
              className={classNames(styles["controls-header"], {
                [styles["mobile-header"]]: props.breakingPoints.isMobile,
                [styles.shadow]:
                  !shortOrgConfig.continueConversationOnMobileEnabled && !uiStore.mainNotification
              })}
            >
              <div className={styles["controls-container"]}>
                <AgentsHeader
                  agent={assignedAgent.isBot ? {...assignedAgent, lastName: ""} : assignedAgent}
                  subtext={
                    assignedAgent.isBot ? assignedAgent.lastName :
                    askForMicrophone
                      ? intl.formatMessage("widget.call.yourCameraWillStayOff")
                      : shortOrgConfig.name
                  }
                />
                <div className={styles.controls}>
                  {(((!foreCallStore.isNoCall && foreCallStore.videoExpanded) ||
                    shortOrgConfig.callsDisabled) && <div />) ||
                    // TODO: move to separate component - CallButton
                    (foreCallStore.localMicrophone.isPermissionDenied && (
                      <>
                        <i
                          className={["icon-crossed-mic"]
                            .concat([styles["mic-permission"]])
                            .join(" ")}
                        />
                        <div className={styles["permission-description"]}>
                          {
                            (foreCallStore.localMicrophone.isPermissionDenied && (
                              <L10n id={"widget.call.changeMicPermissions"} />
                            )) || <L10n id={"widget.call.giveMicPermission"} /> // TODO: show this message
                          }
                        </div>
                      </>
                    )) ||
                    (askForMicrophone && (
                      <WidgetButton
                        buttonStyle={"secondary orange"}
                        onClick={handleCallClicked}
                        small={props.breakingPoints.isMobile}
                      >
                        <L10n id={"widget.call.allowMicAccess"} />
                      </WidgetButton>
                    )) ||
                    renderCallButton(
                      assignedAgent,
                      !isAudioCallSupported || (assignedAgent && !assignedAgentAvailable)
                    )}
                  {shortOrgConfig.forgetMeEnabled && <WidgetMenu forgetMe={props.forgetMeAction} />}
                </div>
              </div>
            </div>
            {shortOrgConfig.continueConversationOnMobileEnabled && (
              <div className={classNames(styles["continue-container"], styles.shadow)}>
                <WidgetButton
                  buttonStyle={"secondary"}
                  fullWidth
                  onClick={continueConversationOnOtherDevice}
                >
                  <L10n id={"widget.continueOnMobileLink"} />
                </WidgetButton>
              </div>
            )}
          </>
        )}
      </>
    );
  };

  const renderNoAssignee = (coverImageUrl?: string) => {
    const defaultMessage = intl.formatMessage("widget.chat.welcomeMessage", {
      companyName: shortOrgConfig.name
    });
    const welcomeMessageText = shortOrgConfig.headerWelcomeMessage || defaultMessage;

    return (
      <div
        className={classNames(styles["controls-header"], styles["welcome-header"])}
        style={
          coverImageUrl
            ? {
                backgroundImage: `linear-gradient(rgba(0,0,0,var(--widget-header-cover-image-gradient-alpha)),rgba(0,0,0,var(--widget-header-cover-image-gradient-alpha))), url(${coverImageUrl})`
              }
            : {}
        }
      >
        {shortOrgConfig.logoToken && (
          <div className={styles.logo}>
            <ImageComponent src={getWheelhouseDownloadUrl(shortOrgConfig.logoToken)} />
          </div>
        )}
        {welcomeMessageText}
      </div>
    );
  };

  return render();
});
